import { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyWallets } from '../../api/walletApi';
import { getMyReferrals, getLevelUsers } from '../../api/userApi';

const accent = '#cc66ff';
const cyan = '#00e5ff';
const fmt = (v) => v != null ? Number(v).toFixed(2) : '—';
const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(153,51,255,0.55) 0%, rgba(20,0,40,0.9) 45%, #0d0d0d 70%)',
  color: '#fff',
  border: '1px solid #9933ff44',
};

const StatCard = ({ value, label, valueColor = accent, onClick, footerAction, xs = 12, md = 4 }) => (
  <Col xs={xs} md={md}>
    <Card style={{ ...cardDark, position: 'relative' }} className="p-3 h-100 dashboard-card">
      <Card.Body>
        <div style={{ fontSize: 28, fontWeight: 800, color: valueColor, marginBottom: 4 }}>
          {value}
        </div>
        <div style={{ color: '#aaa', fontSize: 13 }}>{label}</div>
        {footerAction && <div style={{ marginTop: 10 }}>{footerAction}</div>}
        {onClick && (
          <span
            onClick={onClick}
            style={{ position: 'absolute', bottom: 12, right: 16, color: cyan, fontSize: 22, fontWeight: 300, cursor: 'pointer', lineHeight: 1, zIndex: 2 }}
          >
            ›
          </span>
        )}
      </Card.Body>
    </Card>
  </Col>
);


const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wallets, setWallets] = useState({ main_wallet: null, earning_wallet: null, one_wallet: null });
  const [referralCount, setReferralCount] = useState(null);
  const [levelUserCount, setLevelUserCount] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyTimeout = useRef(null);

  const referralLink = `https://app.onecircle.biz/register?ref=${user?.userCode || ''}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      clearTimeout(copyTimeout.current);
      copyTimeout.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    getMyWallets()
      .then((res) => setWallets(res.data.wallets))
      .catch(() => setWallets({ main_wallet: { balance: 0 }, earning_wallet: { balance: 0 }, one_wallet: { balance: 0 } }));
    getMyReferrals()
      .then((res) => setReferralCount(res.data.count))
      .catch(() => setReferralCount(0));
    getLevelUsers()
      .then((res) => setLevelUserCount(res.data.count))
      .catch(() => setLevelUserCount(0));
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">

        {/* Header */}
        <div className="mb-4">
          <h4 style={{ color: '#fff', fontWeight: 700 }}>Welcome, {user?.name}</h4>
          <div style={{ color: '#aaa', fontSize: 13 }}>Your OneCircle dashboard</div>
        </div>

        <Row className="g-3">

          {/* 1. Profile — full width mobile, 1/3 desktop */}
          <Col xs={12} md={4}>
            <Card style={{ ...cardDark, border: '1px solid #9933ff88' }} className="p-3 h-100 dashboard-card">
              <Card.Body>
                <div style={{ fontSize: 14, fontWeight: 700, color: accent, marginBottom: 12 }}>Profile</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Name</div>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{user?.name}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>User Code</div>
                    <div style={{ color: cyan, fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{user?.userCode || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Performance Rank</div>
                    <div style={{ color: '#ffaa44', fontSize: 13, fontWeight: 600 }}>—</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Slot Active</div>
                    <div style={{ color: '#66ff99', fontSize: 13, fontWeight: 600 }}>{user?.planId?.planName || '—'}</div>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <Button
                      size="sm"
                      onClick={() => navigate('/slots')}
                      style={{ background: accent, border: 'none', color: '#000', fontWeight: 600, fontSize: 12 }}
                    >
                      Upgrade Slot
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* 2. Main Wallet — half mobile, 1/3 desktop */}
          <StatCard
            value={<>{fmt(wallets.main_wallet?.balance)} <span style={{ fontSize: 14, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Main Wallet"
            valueColor="#66aaff"
            onClick={() => navigate('/wallet')}
            footerAction={
              <button
                onClick={(e) => { e.stopPropagation(); navigate('/wallet?addFunds=1'); }}
                style={{ background: 'rgba(102,255,153,0.15)', border: '1px solid rgba(102,255,153,0.3)', color: '#66ff99', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >
                + Add Funds
              </button>
            }
            xs={6} md={4}
          />

          {/* 3. One Wallet — half mobile, 1/3 desktop */}
          <StatCard
            value={<>{fmt(wallets.one_wallet?.balance)} <span style={{ fontSize: 14, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="One Wallet"
            valueColor="#ff88cc"
            onClick={() => navigate('/wallet?type=one')}
            xs={6} md={4}
          />

          {/* 4. Earning Wallet — full width mobile, 1/3 desktop */}
          <StatCard
            value={<>{fmt(wallets.earning_wallet?.balance)} <span style={{ fontSize: 14, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Earning Wallet"
            valueColor="#66ff99"
            onClick={() => navigate('/wallet?type=earning')}
            xs={12} md={4}
          />

          {/* 5. Direct Referrals — half mobile, 1/3 desktop */}
          <StatCard
            value={referralCount !== null ? referralCount : '—'}
            label="My Direct Referrals"
            valueColor={accent}
            onClick={() => navigate('/referrals')}
            xs={6} md={4}
          />

          {/* 6. Level Users — half mobile, 1/3 desktop */}
          <StatCard
            value={levelUserCount !== null ? levelUserCount : '—'}
            label="Level Users"
            valueColor="#ffaa44"
            onClick={() => navigate('/level-users')}
            xs={6} md={4}
          />

          {/* 7. Fastrack Bonus */}
          <StatCard
            value={<>0.00 <span style={{ fontSize: 14, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Fastrack"
            valueColor={accent}
            onClick={() => navigate('/fastrack')}
            xs={6} md={4}
          />

          {/* 8. Generation Bonus */}
          <StatCard
            value={<>0.00 <span style={{ fontSize: 14, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Generation"
            valueColor="#66aaff"
            onClick={() => navigate('/generation-bonus')}
            xs={6} md={4}
          />

          {/* 9. Performance Bonus */}
          <StatCard
            value={<>0.00 <span style={{ fontSize: 14, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Performance"
            valueColor="#ffaa44"
            onClick={() => navigate('/performance-bonus')}
            xs={6} md={4}
          />

          {/* 10. Rewards */}
          <StatCard
            value={<>0.00 <span style={{ fontSize: 14, color: '#aaa', fontWeight: 400 }}>USDT</span></>}
            label="Reward"
            valueColor="#66ff99"
            onClick={() => navigate('/rewards')}
            xs={6} md={4}
          />

          {/* 11. Referral Link — visible only after Slot 1 is purchased */}
          {user?.planId && <Col xs={12} md={12}>
            <Card style={{ ...cardDark, border: `1px solid ${cyan}44` }} className="p-3 dashboard-card">
              <Card.Body>
                <div style={{ fontSize: 14, fontWeight: 700, color: cyan, marginBottom: 10 }}>
                  Your Referral Link
                </div>
                <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
                  Share this link to invite others. Your code:{' '}
                  <strong style={{ color: '#fff' }}>{user?.userCode || '—'}</strong>
                </div>
                <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: 'rgba(0,229,255,0.06)',
                      border: `1px solid ${cyan}33`,
                      borderRadius: 6,
                      padding: '8px 14px',
                      fontSize: 13,
                      color: '#ccc',
                      fontFamily: 'monospace',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={referralLink}
                  >
                    {referralLink}
                  </div>
                  <Button
                    size="sm"
                    onClick={handleCopy}
                    style={{
                      background: copied ? 'rgba(0,229,255,0.18)' : 'transparent',
                      border: `1px solid ${cyan}`,
                      color: cyan,
                      fontWeight: 600,
                      minWidth: 80,
                      transition: 'background 0.2s',
                    }}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>}

        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;
